/**
 * ShpfltSocialProof — Horizontal video-testimonial carousel with center autoplay.
 * CSS scroll-snap handles centering; JS plays the centered video and handles clicks.
 *
 * @example
 * <shpflt-social-proof data-section-id="{{ section.id }}">
 *   ...
 * </shpflt-social-proof>
 */

import { ShopigurusHTMLElement } from '@shopigurus/base';

class ShpfltSocialProof extends ShopigurusHTMLElement {
  /** @type {Map<HTMLElement, HTMLElement>} card → video-media */
  #videoMap = new Map();

  /** @type {number} */
  #currentIndex = 0;

  /** @type {HTMLElement | null} */
  #currentlyPlayingCard = null;

  /** @type {number} */
  #scrollTimeout = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {{ start: boolean, end: boolean }} */
  #atEdge = { start: true, end: false };

  connectedCallback() {
    super.connectedCallback();
    const signal = this.abortController.signal;

    this.#initializeCards();
    this.#setupCardNavigation(signal);
    this.#setupMuteButtons(signal);
    this.#setupPlayIndicators(signal);
    this.#setupScrollListener(signal);
    this.#setupMediaStateSync(signal);
    this.#setupResizeObserver();

    requestAnimationFrame(() => {
      this.#updateScrollableState();
      this.#initEdgeState();
      this.#playNearestCenter();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    clearTimeout(this.#scrollTimeout);
    this.#videoMap.clear();
    this.#currentlyPlayingCard = null;
  }

  // ─── Public API (carousel prev/next buttons) ──────────────────────────────

  get selectedIndex() { return this.#currentIndex; }

  previous({ instant = false } = {}) {
    this.scrollBy({ left: -this.#getStride(), behavior: instant ? 'auto' : 'smooth' });
  }

  next({ instant = false } = {}) {
    this.scrollBy({ left: this.#getStride(), behavior: instant ? 'auto' : 'smooth' });
  }

  get cells() {
    return Array.from(this.querySelectorAll('.social-proof-card'));
  }

  // ─── Initialization ───────────────────────────────────────────────────────

  #initializeCards() {
    for (const card of this.cells) {
      card.setAttribute('data-playing', 'false');
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  /**
   * Centers a card using viewport-relative rects.
   * @param {HTMLElement} card
   * @param {boolean} [instant=false]
   */
  #scrollToCard(card, instant = false) {
    const containerRect = this.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const cardScrollLeft = cardRect.left - containerRect.left + this.scrollLeft;
    const targetScrollLeft = cardScrollLeft - (this.clientWidth - card.offsetWidth) / 2;
    this.scrollTo({ left: targetScrollLeft, behavior: instant ? 'auto' : 'smooth' });
  }

  /**
   * Width of one card slot (card + gap) via rect diff.
   * @returns {number}
   */
  #getStride() {
    const cards = this.cells;
    if (cards.length < 2) return cards[0]?.offsetWidth ?? 0;
    const r0 = cards[0].getBoundingClientRect();
    const r1 = cards[1].getBoundingClientRect();
    return r1.left - r0.left;
  }

  /** @param {AbortSignal} signal */
  #setupCardNavigation(signal) {
    this.addEventListener('click', (e) => {
      const card = e.target.closest('.social-proof-card');
      if (!card) return;
      this.#scrollToCard(card);
      // Play immediately — don't wait for smooth-scroll + debounce to settle
      this.#setActiveCard(card);
    }, { signal });
  }

  /**
   * Pauses the currently playing card and plays the given one.
   * Also marks the given card as the active slide so the CSS can style it.
   * @param {HTMLElement} card
   */
  #setActiveCard(card) {
    if (card === this.#currentlyPlayingCard) return;
    for (const other of this.cells) {
      if (other !== card) other.removeAttribute('data-active');
    }
    card.setAttribute('data-active', 'true');
    if (this.#currentlyPlayingCard) this.#pauseVideo(this.#currentlyPlayingCard);
    this.#playVideo(card);
    this.#currentlyPlayingCard = card;
  }

  // ─── Scroll handling ──────────────────────────────────────────────────────

  #setupScrollListener(signal) {
    this.addEventListener('scroll', () => {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = setTimeout(() => this.#handleScrollEnd(), 100);
    }, { signal, passive: true });
  }

  #handleScrollEnd() {
    this.#updateCurrentIndex();
    this.#updateScrollableState();
    this.#updateEdgeState();
    this.#playNearestCenter();
  }

  // ─── Video playback ───────────────────────────────────────────────────────

  /**
   * Plays the video nearest to center; pauses all others.
   * @fires document#social-proof:center-changed
   */
  #playNearestCenter() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const containerCenter = this.getBoundingClientRect().left + this.clientWidth / 2;
    let closestCard = null;
    let closestDist = Infinity;

    for (const card of this.cells) {
      const rect = card.getBoundingClientRect();
      const dist = Math.abs(rect.left + rect.width / 2 - containerCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestCard = card;
      }
    }

    if (!closestCard || closestCard === this.#currentlyPlayingCard) return;

    this.#setActiveCard(closestCard);

    document.dispatchEvent(new CustomEvent('social-proof:center-changed', {
      bubbles: true,
      detail: { card: closestCard },
    }));
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  #getVideoMedia(card) {
    if (!this.#videoMap.has(card)) {
      const vm = card.querySelector('video-media');
      if (vm) {
        customElements.upgrade(vm);
        this.#videoMap.set(card, vm);
      }
    }
    return this.#videoMap.get(card) ?? null;
  }

  /** @param {HTMLElement} card */
  #playVideo(card) {
    const vm = this.#getVideoMedia(card);
    if (!vm) return;
    vm.play();
    card.setAttribute('data-playing', 'true');
    card.querySelector('[data-play-indicator]')?.setAttribute('aria-label', 'Pause video');
  }

  /** @param {HTMLElement} card */
  #pauseVideo(card) {
    const vm = this.#getVideoMedia(card);
    if (!vm) return;
    vm.pause();
    card.setAttribute('data-playing', 'false');
    card.querySelector('[data-play-indicator]')?.setAttribute('aria-label', 'Play video');
  }

  // ─── UI controls ─────────────────────────────────────────────────────────

  /** @param {AbortSignal} signal */
  #setupMuteButtons(signal) {
    for (const btn of this.querySelectorAll('[data-mute-btn]')) {
      btn.addEventListener('click', (e) => this.#handleMuteToggle(e), { signal });
    }
  }

  /**
   * @param {MouseEvent} e
   * @fires document#social-proof:mute-toggled
   */
  #handleMuteToggle(e) {
    e.stopPropagation();
    const btn = e.currentTarget;
    const card = btn.closest('.social-proof-card');
    const video = this.#getVideoMedia(card)?.querySelector('video');
    if (!video) return;

    const isMuted = btn.getAttribute('aria-pressed') === 'true';
    video.muted = !isMuted;
    btn.setAttribute('aria-pressed', String(!isMuted));
    btn.setAttribute('aria-label', isMuted ? 'Sound on' : 'Sound off');

    document.dispatchEvent(new CustomEvent('social-proof:mute-toggled', {
      bubbles: true,
      detail: { muted: !isMuted, video },
    }));
  }

  /** @param {AbortSignal} signal */
  #setupPlayIndicators(signal) {
    for (const btn of this.querySelectorAll('[data-play-indicator]')) {
      btn.addEventListener('click', (e) => this.#handlePlayToggle(e), { signal });
    }
  }

  /**
   * @param {MouseEvent} e
   * @fires document#social-proof:play-toggled
   */
  #handlePlayToggle(e) {
    e.stopPropagation();
    const indicator = e.currentTarget;
    const card = indicator.closest('.social-proof-card');
    const vm = this.#getVideoMedia(card);
    if (!vm) return;

    // If not centered, scroll it in AND start playing now.
    // Waiting for scroll-end autoplay breaks when the card is edge-clamped
    // (e.g. last card in the list), because #playNearestCenter would pick a
    // different card as the closest-to-center.
    if (!this.#isCardCentered(card)) {
      this.#scrollToCard(card);
      this.#setActiveCard(card);
      return;
    }

    const isPlaying = card.getAttribute('data-playing') === 'true';
    if (isPlaying) {
      // Pause but keep this card marked active so the play icon reappears
      this.#pauseVideo(card);
      this.#currentlyPlayingCard = null;
    } else {
      // Route through #setActiveCard so data-active moves to this card and icons update
      this.#setActiveCard(card);
    }

    document.dispatchEvent(new CustomEvent('social-proof:play-toggled', {
      bubbles: true,
      detail: { playing: !isPlaying, videoMedia: vm },
    }));
  }

  /**
   * True when the card is centered, OR when it's an edge card whose "ideal center"
   * scroll position is clamped by the container's scroll bounds (i.e. it's
   * already as centered as it can physically get).
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  #isCardCentered(card) {
    const containerRect = this.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    const cardCenter = cardRect.left + cardRect.width / 2;

    if (Math.abs(cardCenter - containerCenter) < cardRect.width / 2) return true;

    // Edge-clamp fallback: first/last cards can never truly center on overflow-constrained
    // containers. Treat them as centered if scrollLeft is already at the nearest reachable point.
    const desired = cardRect.left - containerRect.left + this.scrollLeft - (this.clientWidth - card.offsetWidth) / 2;
    const maxScroll = this.scrollWidth - this.clientWidth;
    const clamped = Math.max(0, Math.min(maxScroll, desired));
    return Math.abs(this.scrollLeft - clamped) < 1;
  }

  // ─── State tracking ───────────────────────────────────────────────────────

  #updateCurrentIndex() {
    const cards = this.cells;
    if (!cards.length) return;

    const containerCenter = this.getBoundingClientRect().left + this.clientWidth / 2;
    let closestIndex = 0;
    let closestDist = Infinity;

    cards.forEach((card, i) => {
      const rect = card.getBoundingClientRect();
      const dist = Math.abs(rect.left + rect.width / 2 - containerCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    if (closestIndex !== this.#currentIndex) {
      this.#currentIndex = closestIndex;
      this.dispatchEvent(new CustomEvent('carousel:change', {
        bubbles: true,
        detail: { index: closestIndex, cell: cards[closestIndex] },
      }));
    }
  }

  #updateScrollableState() {
    this.classList.toggle('is-scrollable', this.scrollWidth > this.clientWidth);
  }

  #initEdgeState() {
    const atStart = this.scrollLeft <= 5;
    const atEnd = Math.ceil(this.scrollLeft + this.clientWidth) >= this.scrollWidth - 5;
    this.#atEdge = { start: atStart, end: atEnd };
    if (atStart) this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { bubbles: true, detail: { position: 'start' } }));
    if (atEnd) this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { bubbles: true, detail: { position: 'end' } }));
  }

  #updateEdgeState() {
    const tolerance = 5;
    const atStart = this.scrollLeft <= tolerance;
    const atEnd = Math.ceil(this.scrollLeft + this.clientWidth) >= this.scrollWidth - tolerance;

    if (atStart !== this.#atEdge.start) {
      this.dispatchEvent(new CustomEvent(atStart ? 'scroll:edge-nearing' : 'scroll:edge-leaving', { bubbles: true, detail: { position: 'start' } }));
      this.#atEdge.start = atStart;
    }
    if (atEnd !== this.#atEdge.end) {
      this.dispatchEvent(new CustomEvent(atEnd ? 'scroll:edge-nearing' : 'scroll:edge-leaving', { bubbles: true, detail: { position: 'end' } }));
      this.#atEdge.end = atEnd;
    }
  }

  #setupMediaStateSync(signal) {
    this.addEventListener('media:play', (e) => {
      e.target.closest('.social-proof-card')?.setAttribute('data-playing', 'true');
    }, { signal });
    this.addEventListener('media:pause', (e) => {
      e.target.closest('.social-proof-card')?.setAttribute('data-playing', 'false');
    }, { signal });
  }

  #setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    this.#resizeObserver = new ResizeObserver(() => this.#updateScrollableState());
    this.#resizeObserver.observe(this);
  }

  // ─── Shopify editor events ────────────────────────────────────────────────

  onSectionSelect() {
    for (const vm of this.querySelectorAll('video-media')) {
      customElements.upgrade(vm);
      vm.pause();
    }
    this.#currentlyPlayingCard = null;
  }

  onSectionDeselect() {
    this.#playNearestCenter();
  }

  onBlockSelect(e) {
    const blockId = e.detail?.blockId;
    if (!blockId) return;
    const block = this.querySelector(`[data-block-id="${blockId}"]`);
    if (block) this.#scrollToCard(block);
  }
}

customElements.define('shpflt-social-proof', ShpfltSocialProof);
